import type {
  SearchCriteriaFilterSchema,
  SearchCriteriaRangeFilterSchema,
  SearchSearchCriteriaSchema,
} from '@iconik-io/iconik-api/src/services/search';

import type { FacetsSchema } from '../common_list/types';

export type FacetValues = { [name: string]: string[] };

export type FilterValueOperator = 'AND' | 'NOT';

export type FilterValueStoredIn = {
  operator: FilterValueOperator;
  storageId: string;
};

export type FilterValueMultiDropdown = {
  value: string;
  label: string;
};

export const INCLUDE_ALL_OF = 'include_all_of';
export const INCLUDE_ANY_OF = 'include_any_of';
export const EXCLUDE_ALL_OF = 'exclude_all_of';
export const EXCLUDE_ANY_OF = 'exclude_any_of';

export type MultiSelectFilterModifier =
  | typeof INCLUDE_ALL_OF
  | typeof INCLUDE_ANY_OF
  | typeof EXCLUDE_ALL_OF
  | typeof EXCLUDE_ANY_OF;

export const MULTI_SELECT_FILTER_MODIFIERS: MultiSelectFilterModifier[] = [
  INCLUDE_ALL_OF,
  INCLUDE_ANY_OF,
  EXCLUDE_ALL_OF,
  EXCLUDE_ANY_OF,
];

export const toMultiSelectFilterModifier = (
  value: string | undefined,
): MultiSelectFilterModifier =>
  MULTI_SELECT_FILTER_MODIFIERS.find((modifier) => modifier === value) ??
  INCLUDE_ANY_OF;

export type MultiSelectFilterGroup = {
  modifier: MultiSelectFilterModifier;
  items: string[];
  // Per-item override of the ES term name (storageId → termName).
  // When absent, the caller-supplied termName is used for all items.
  itemTermNames?: Record<string, string>;
  // Additional flat term names that are always OR:ed in alongside the
  // per-purpose nested terms (e.g. 'storage_id' to also match collections).
  additionalTermNames?: string[];
};

export type MultiSelectFilterValue = MultiSelectFilterGroup[];

export type FilterValue =
  | boolean
  | string
  | string[]
  | SearchCriteriaRangeFilterSchema
  | FilterValueStoredIn[]
  | FilterValueMultiDropdown[]
  | MultiSelectFilterValue;

export type FilterValues = {
  [name: string]: FilterValue;
};

export type CanonicalFilterMapping = {
  canonicalKey: string;
  nestedKeys: Set<string>;
};

export type CommonFiltersAppState = {
  query: string;
  availableFacets: FacetsSchema;
  filterValues: FilterValues;
  facetValues: FacetValues;

  // Names (keys into filterValues and/or facetValues) that are currently
  // toggled off via the filter chip "eye" control. Disabled filters stay in
  // state so they can be re-enabled, but are excluded from the search request.
  disabledFilters?: string[];

  // Filters that don't have UI representation, they intended to be used as is
  // in search requests or when updating a previously loaded saved search
  // for example.
  unsupportedFilters: SearchCriteriaFilterSchema[];

  // Raw criteria is set when user first time opens a saved search and unset
  // once user changes any filter, facet or query. This is used to pass through an
  // original saved search criteria for cases when UI can't represent a criteria.
  // Without raw criteria set, all filter values, facet values and query will be parsed
  // into the UI controls and combined into a search request criteria.
  //
  // Raw criteria set:
  // Original criteria --> Search request
  //
  // Raw criteria unset:
  // Original criteria --> Parsed to UI --> Collected from UI --> Search request
  rawCriteria: SearchSearchCriteriaSchema | undefined;
};
